// Local-file session persistence — replaces the old Firestore cloud save.
// A session is a plain JSON file the user owns on their own disk; nothing
// is stored on our servers. Uses the File System Access API where available
// (real Save/Open with a re-usable file handle, like a desktop editor) and
// falls back to download/upload elsewhere.
//
// This module runs outside React (called from InfiniteCanvas.js callbacks,
// but has no hook access itself) — it accepts a `t` translate function
// (InfiniteCanvas.js's own useT()) as an optional last argument instead of
// importing tStatic, so its copy tracks the current user/profile language
// exactly like the rest of the canvas UI, not just the browser/localStorage
// guess tStatic falls back to. When no `t` is passed (e.g. from a future
// test that only exercises the file-handling logic), DEFAULT_T below
// supplies the original Italian strings so behavior stays sensible.
const DEFAULT_T = (key) => ({
  "canvas.sessionFile.pickerDescription": "Sessione Sinergie",
  "canvas.sessionFile.defaultName": "sessione",
  "canvas.sessionFile.invalidFile": "File non valido",
  "canvas.sessionFile.notASession": "Questo file non è una sessione Sinergie",
  "canvas.sessionFile.corrupted": "Sessione corrotta: nodi mancanti",
}[key] || key);

export const SESSION_FILE_FORMAT = "sinergie-session";
export const SESSION_FILE_VERSION = 1;
const FILE_EXT = ".sinergie.json";

// Checked at call time (not module load) so it reflects the actual runtime
// and stays testable.
function fsAvailable() {
  return (
    typeof window !== "undefined" &&
    typeof window.showSaveFilePicker === "function" &&
    typeof window.showOpenFilePicker === "function"
  );
}

function suggestedName(snapshot, t) {
  const base = (snapshot && snapshot.name && snapshot.name !== "default_name"
    ? snapshot.name
    : t("canvas.sessionFile.defaultName")
  ).replace(/[^\w-]+/g, "_");
  return `${base}${FILE_EXT}`;
}

function wrap(snapshot) {
  return JSON.stringify(
    { _format: SESSION_FILE_FORMAT, _version: SESSION_FILE_VERSION, ...snapshot },
    null,
    2
  );
}

function triggerDownload(text, filename) {
  const blob = new Blob([text], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke on the next tick so the download has started.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

// Writes a session snapshot to disk. When `handle` is a live FileSystem
// FileHandle (from a prior save/open) it re-saves to that same file
// silently; otherwise it prompts for a location (FS Access) or downloads
// (fallback). Returns { handle, name, downloaded } — handle is null in
// fallback mode. Throws only on a genuine write error; a user cancelling
// the picker resolves to null so callers can treat it as a no-op.
export async function saveSessionToFile(snapshot, handle = null, t = DEFAULT_T) {
  const text = wrap(snapshot);
  const name = suggestedName(snapshot, t);

  if (fsAvailable()) {
    let fileHandle = handle;
    try {
      if (!fileHandle) {
        fileHandle = await window.showSaveFilePicker({
          suggestedName: name,
          types: [{ description: t("canvas.sessionFile.pickerDescription"), accept: { "application/json": [FILE_EXT, ".json"] } }],
        });
      }
      const writable = await fileHandle.createWritable();
      await writable.write(text);
      await writable.close();
      return { handle: fileHandle, name: fileHandle.name || name, downloaded: false };
    } catch (e) {
      if (e && e.name === "AbortError") return null; // user cancelled
      throw e;
    }
  }

  triggerDownload(text, name);
  return { handle: null, name, downloaded: true };
}

function validate(parsed, t) {
  if (!parsed || typeof parsed !== "object") throw new Error(t("canvas.sessionFile.invalidFile"));
  if (parsed._format && parsed._format !== SESSION_FILE_FORMAT) {
    throw new Error(t("canvas.sessionFile.notASession"));
  }
  if (!Array.isArray(parsed.nodes)) throw new Error(t("canvas.sessionFile.corrupted"));
  return parsed;
}

// Opens a session file chosen by the user and returns { data, handle, name }.
// `handle` is a re-usable FileSystemFileHandle (FS Access) or null (fallback),
// so a later "Salva" can write back to the same file. Returns null if the
// user cancels. Throws on parse/validation failure.
export async function openSessionFromFile(t = DEFAULT_T) {
  if (fsAvailable()) {
    let fileHandle;
    try {
      [fileHandle] = await window.showOpenFilePicker({
        types: [{ description: t("canvas.sessionFile.pickerDescription"), accept: { "application/json": [FILE_EXT, ".json"] } }],
        multiple: false,
      });
    } catch (e) {
      if (e && e.name === "AbortError") return null;
      throw e;
    }
    const file = await fileHandle.getFile();
    const data = validate(JSON.parse(await file.text()), t);
    return { data, handle: fileHandle, name: fileHandle.name || file.name };
  }

  // Fallback: hidden file input.
  return new Promise((resolve, reject) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".json,application/json";
    input.style.display = "none";
    document.body.appendChild(input);
    let settled = false;
    input.addEventListener("change", async () => {
      settled = true;
      const file = input.files && input.files[0];
      input.remove();
      if (!file) return resolve(null);
      try {
        const data = validate(JSON.parse(await file.text()), t);
        resolve({ data, handle: null, name: file.name });
      } catch (e) {
        reject(e);
      }
    });
    // If the dialog is dismissed with no selection, resolve null once focus
    // returns (change never fires on cancel).
    window.addEventListener(
      "focus",
      () => setTimeout(() => { if (!settled) { input.remove(); resolve(null); } }, 300),
      { once: true }
    );
    input.click();
  });
}
